import { Injectable } from '@angular/core';
import { Auth } from '@freelancer/auth';
import { CCFormExtraPayload } from '@freelancer/billing';
import { BackendErrorTypes } from '@freelancer/datastore/backend-error.types';
import {
  FreelancerHttp,
  ResponseData,
  SuccessResponseData,
} from '@freelancer/freelancer-http';
import { Location } from '@freelancer/location';
import {
  OverlayTypes,
  PaymentsErrorType,
  PaymentsEventType,
  PaymentsMessagingService,
  PaymentsResultStatus,
} from '@freelancer/payments-messaging';
import { PaymentsPopUpService } from '@freelancer/payments-popup';
import { PaymentsUtils } from '@freelancer/payments-utils';
import { Pwa } from '@freelancer/pwa';
import { Tracking } from '@freelancer/tracking';
import { assertNever, toNumber } from '@freelancer/utils';
import {
  InAppBrowser,
  InAppBrowserObject,
} from '@laurentgoudet/ionic-native-in-app-browser/ngx';
import {
  DepositConfirmResponseApi,
  DepositMethodApi,
  DepositRequestActionApi,
  DepositRequestApi,
  DepositRequestConfirmationApi,
  ReferenceDepositRequestApi,
  ThreedsCapabilityApi,
  ThreedsDataApi,
  ThreedsResponseDataApi,
} from 'api-typings/payments/payments';
import * as Rx from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import {
  PreprocessMilestonesRequest,
  PreprocessMilestonesResponse,
  ThreeDsBaseContext,
  ThreeDSContext,
} from './deposits.model';
import { ThreeDSState } from './deposits.types';

/**
 * Form data item
 */
export interface FormDataItem {
  name: string;
  value: string;
}

/**
 * 3DS messages types posted from popup window
 */
enum ThreeDSMessageType {
  TRIGGER = 'breakTo3ds',
  RETURN = 'returnFrom3DS',
}

@Injectable({
  providedIn: 'root',
})
export class Deposits {
  private inAppBrowserMessageSubscription?: Rx.Subscription;
  private browser: InAppBrowserObject;
  private threeDSPopUp: Window;
  /**
   * a unique id of the deposit so that we can ensure that if
   * someone calls creditCardDeposit multiple times
   * we only finish processing the most recent call
   */
  private depositReference?: number;
  constructor(
    private auth: Auth,
    private freelancerHttp: FreelancerHttp,
    private paymentsMessaging: PaymentsMessagingService,
    private paymentsUtils: PaymentsUtils,
    private location: Location,
    private inAppBrowser: InAppBrowser,
    private pwa: Pwa,
    private popUpService: PaymentsPopUpService,
    private tracking: Tracking,
  ) {}

  /**
   * Deposit request setup through API
   *
   * @param payload Post request payload for deposit API
   */
  deposit(payload: DepositRequestApi) {
    return this.auth.getUserId().pipe(
      switchMap(userId =>
        this.freelancerHttp.post<DepositRequestActionApi>(
          'payments/0.1/deposits',
          { ...payload, user_id: toNumber(userId) },
        ),
      ),
      take(1),
    );
  }

  /**
   * Deposit request confirm through API
   *
   * @param payload Post request payload for deposit API
   */
  depositUpdate(
    payload: DepositRequestConfirmationApi,
  ): Promise<
    ResponseData<
      DepositConfirmResponseApi,
      BackendErrorTypes['deposit_confirm'] | 'UNKNOWN_ERROR'
    >
  > {
    return this.auth
      .getUserId()
      .pipe(
        switchMap(userId =>
          // Response is {status: 'success', requestId: ...}
          this.freelancerHttp.put<
            DepositConfirmResponseApi,
            BackendErrorTypes['deposit_confirm']
          >('payments/0.1/deposits', {
            ...payload,
            user_id: toNumber(userId),
          }),
        ),
        take(1),
      )
      .toPromise();
  }

  /**
   * Recurring payment through API
   *
   * @param depositMethod Recurring payment method
   * @param billingAgreementId Billing agreement ID for recurring payment
   * @param amount The amount for recurring payment
   * @param depositCurrencyId The currency ID for deposit
   * @param paymentCurrencyId The currency ID for recurring payment
   * @param threatMetrixSessionId
   * @param trackingToken The token for tracking purpose
   * @param cartId The ID for cart that need to be paid
   * @param creditCardGateway recurring payment gateway
   */
  authorization(
    depositMethod: DepositMethodApi,
    billingAgreementId: string,
    amount: number,
    depositCurrencyId: number,
    paymentCurrencyId: number,
    threatMetrixSessionId: string,
    trackingToken: string,
    cartId?: number,
    creditCardGateway?: string,
  ) {
    return this.auth.getUserId().pipe(
      switchMap(userId => {
        const payload = this.getAuthorizationPayload(
          toNumber(userId),
          depositMethod,
          billingAgreementId,
          amount,
          depositCurrencyId,
          paymentCurrencyId,
          threatMetrixSessionId,
          trackingToken,
          creditCardGateway,
          cartId,
        );
        // Response is {status: 'success', requestId: ...}
        return this.freelancerHttp.put<undefined>(
          'payments/0.1/authorization',
          payload,
        );
      }),
      take(1),
    );
  }

  /**
   * This should be the ultimate method for constructing the deposit call payload.
   * FIXME: maybe "ultimate" but unused?
   *
   * TODO: replace the methods.component `genericPayload` functionality with this.
   */
  /* private getDepositPayload(
    userId: number,
    depositMethod: DepositMethodApi,
    action: string,
    amount: number,
    currencyId: number,
    backUrl?: string,
    cancelUrl?: string,
    trackingToken?: string,
    threatmetrixSession?: string,
    countryCode?: string,
    creditCardGateway?: string,
    boletoInfo?: BoletoInfoApi,
  ): DepositRequestApi {
    return {
      user_id: userId,
      deposit_method: depositMethod,
      action,
      back_url: backUrl,
      cancel_url: cancelUrl,
      amount,
      currency_id: currencyId,
      credit_card_gateway: creditCardGateway,
      domain: '',
      tracking_token: trackingToken,
      threatmetrix_session: threatmetrixSession,
      country_code: countryCode,
      boleto_info: boletoInfo,
    };
  }*/

  /**
   * Populates the browser info fields in CCFormExtraPayload.
   * *Note* Adopted from https://github.com/Adyen/adyen-3ds2-js-utils
   *
   * @param extraPayLoad CCFormExtraPayload object to populate.
   */
  populateBrowserInfo(extraPayLoad: CCFormExtraPayload): CCFormExtraPayload {
    const screen = window && window.screen;
    const navigator = window && window.navigator;
    const language = navigator && navigator.language;

    return {
      ...extraPayLoad,
      browserScreenWidth: screen ? screen.width.toString() : '',
      browserScreenHeight: screen ? screen.height.toString() : '',
      browserColourDepth: screen ? screen.colorDepth.toString() : '',
      browserTZoneOffset: new Date().getTimezoneOffset().toString(),
      browserLanguage: language ? language.toString() : '',
      browserJavaEnabled: navigator?.javaEnabled()?.toString() ?? 'false',
      browserJScriptEnabled: 'true',
    };
  }

  /**
   * Creates a ThreeDSContext object based
   * on the  received ThreedsDataApi.
   *
   * @param threeDSApiData 3DS data field in the response payload to the
   *        DepositConfirm API request.
   * @param currentCtx threeDSContext
   */
  createThreeDSContext(
    threeDSApiData: ThreedsDataApi,
    currentCtx?: ThreeDSContext,
  ): ThreeDSContext | undefined {
    const curState = currentCtx ? currentCtx.type : undefined;

    if (
      !threeDSApiData.threeds_state ||
      !threeDSApiData.invoice_id ||
      !threeDSApiData.credit_card_gateway
    ) {
      return;
    }

    const baseCtx: ThreeDsBaseContext = {
      ccGateway: threeDSApiData.credit_card_gateway,
      invoiceId: threeDSApiData.invoice_id,
      fxTransId: threeDSApiData.fx_transaction_id,
      requestAmount: threeDSApiData.request_amount,
    };

    switch (threeDSApiData.threeds_state) {
      case ThreeDSState.REDIRECT: {
        if (!curState || curState === ThreeDSState.IDENTIFY) {
          const redirectData = threeDSApiData.redirect_flow_data;
          if (!redirectData) {
            return;
          }

          return {
            ...baseCtx,
            type: ThreeDSState.REDIRECT,
            issuerUrl: redirectData.issuer_url,
            issuerReturnUrl: redirectData.issuer_return_url,
            paRequest: redirectData.payer_authentication_request,
            merchantData: redirectData.merchant_data,
            echoData: redirectData.echo_data,
            cookieData: redirectData.cookie_data,
          };
        }
        break;
      }

      case ThreeDSState.IDENTIFY: {
        if (!curState) {
          const identifyData = threeDSApiData.identify_flow_data;
          if (!identifyData) {
            return;
          }

          return {
            ...baseCtx,
            type: ThreeDSState.IDENTIFY,
            serverTransID: identifyData.server_transaction_id,
            issuerUrl: identifyData.issuer_url,
            issuerReturnUrl: identifyData.issuer_return_url,
            threeDS2token: identifyData.threeds2_token,
          };
        }
        break;
      }

      case ThreeDSState.CHALLENGE: {
        if (!curState || curState === ThreeDSState.IDENTIFY) {
          const challengeData = threeDSApiData.challenge_flow_data;
          if (!challengeData) {
            return;
          }

          return {
            ...baseCtx,
            type: ThreeDSState.CHALLENGE,
            serverTransID: challengeData.server_transaction_id,
            acsTransID: challengeData.acs_transaction_id,
            messageVersion: challengeData.message_version,
            messageType: challengeData.message_type,
            issuerUrl: challengeData.issuer_url,
            threeDS2token: challengeData.threeds2_token,
          };
        }
        break;
      }
      default:
    }
  }

  /**
   * Initial 3DS flow
   *
   * @param context threeDSContext
   */
  initiate3DSFlow(context: ThreeDSContext): void {
    switch (context.type) {
      case ThreeDSState.REDIRECT: {
        if (context.issuerReturnUrl === 'gchosted') {
          //
          // GC MerchantLink Fast (Hosted) implementation. We are piggy backing on 3DS V1 'Redirect' to
          // push this feature out quickly. In GC Hosted flow, return URL has be sent to the gateway
          // during the initial payment request, and therefore 'context.issuerReturnUrl' is not required.
          // We are using the special value 'gchosted' switch between the original 'REDIRECT' and
          // 'GC HOSTED' flows.
          //
          // FIXME: Create a new 'context.type' (e.g. 'hosted') instead. This will require
          //

          if (!this.isNative()) {
            this.threeDSPopUp.location.href = context.issuerUrl;
          }
        } else {
          const formItems: FormDataItem[] = [
            { name: 'PaReq', value: context.paRequest },
            { name: 'TermUrl', value: context.issuerReturnUrl },
            { name: 'IsModal', value: 'true' },
          ];
          if (context.merchantData) {
            formItems.push({ name: 'MD', value: context.merchantData });
          }
          this.setupAndPostForm(context.issuerUrl, formItems);
        }

        break;
      }

      case ThreeDSState.IDENTIFY: {
        const dataObj = {
          threeDSServerTransID: context.serverTransID,
          threeDSMethodNotificationURL: context.issuerReturnUrl,
        };

        const encodedJSON = this.encodeBase64URL(JSON.stringify(dataObj));

        const formItems: FormDataItem[] = [
          { name: 'threeDSMethodData', value: encodedJSON },
        ];

        this.setupAndPostForm(context.issuerUrl, formItems);

        break;
      }

      case ThreeDSState.CHALLENGE: {
        // The `challengeWindowSize` describes the popup window (dimensions?) to
        // the issuer. Can be any of the following flags:
        //   01 - 250px x 400px
        //   02 - 390px x 400px
        //   03 - 500px x 600px
        //   04 - 600px x 400px
        //   05 - 100%  x 100%
        const dataObj = {
          threeDSServerTransID: context.serverTransID,
          acsTransID: context.acsTransID,
          messageVersion: context.messageVersion,
          challengeWindowSize: '05',
          messageType: context.messageType,
        };

        const encodedJSON = this.encodeBase64URL(JSON.stringify(dataObj));

        const formItems: FormDataItem[] = [
          { name: 'creq', value: encodedJSON },
        ];

        this.setupAndPostForm(context.issuerUrl, formItems);

        break;
      }

      default:
        return assertNever(context);
    }
    //
  }

  /**
   * Create 3DS context for different 3DS state
   *
   * @param context 3DS context data object
   * @param eventData PostMessage data from Gaf to trigger different 3DS flows
   */
  constructThreeDSResponseApiData(
    context: ThreeDSContext,
    eventData: MessageEvent['data'],
  ): ThreedsResponseDataApi {
    const baseResponseData = {
      threeds_state: context.type,
      fx_transaction_id: context.fxTransId,
      request_amount: context.requestAmount,
    };

    switch (context.type) {
      case ThreeDSState.REDIRECT:
        return {
          ...baseResponseData,
          redirect_flow_results: {
            payer_authentication_response: eventData.paresult,
            merchant_data: context.merchantData,
            echo_data: context.echoData,
            cookie_data: context.cookieData,
          },
        };

      case ThreeDSState.IDENTIFY:
        return {
          ...baseResponseData,
          identify_flow_results: {
            server_transaction_id: eventData.threeDSServerTransID,
            completion_indicator: eventData.threeDSCompInd,
            threeds2_token: context.threeDS2token,
          },
        };

      case ThreeDSState.CHALLENGE:
        return {
          ...baseResponseData,
          challenge_flow_results: {
            server_transaction_id: eventData.threeDSServerTransID,
            transaction_status_indicator: eventData.threeDSTransStatus,
            threeds2_token: context.threeDS2token,
          },
        };

      default:
        return assertNever(context);
    }
  }

  /**
   * Process 'breakTo3ds' events.
   *
   * @remark
   *
   * Depending on the 3ds flow, the backend could send the 'breakTo3ds' event
   * directly to the main window or to the popup window, which then gets
   * posted to the main window. Currently the popup window make sure it's DOM
   * is loaded before dispatching the message to main window. If this process
   * is not followed, a race condition could occur when this routine is
   * modifying popup window's DOM while it is being initialised.
   *
   * @param threeDSPopup The popup window.
   * @param threeDSdata Payload sent with the 'breakTo3ds' event.
   *
   * @privateRemark
   *
   * This is the old 3DS implementation
   *
   * TODO: delete the code below once the deposit flow is moved to API.
   *
   */
  processThreeDSForm(
    threeDSPopup: Window,
    threeDSdata: MessageEvent['data'],
  ): void {
    // Save 3ds data into local storage
    window.localStorage.setItem(
      `cc_data${threeDSdata.invoiceId}`,
      threeDSdata.data,
    );
    window.localStorage.setItem(
      `cc_data_back${threeDSdata.invoiceId}`,
      threeDSdata.backUrl,
    );

    // Set up and submit 3ds form in the popup window
    const returnForm = threeDSPopup.document.createElement('form');
    returnForm.method = 'post';
    returnForm.action = threeDSdata.issuerUrl;

    if (threeDSdata.threeDSState === ThreeDSState.REDIRECT) {
      this.addFormItem(
        threeDSPopup,
        returnForm,
        'PaReq',
        threeDSdata.parequest,
      );
      this.addFormItem(
        threeDSPopup,
        returnForm,
        'TermUrl',
        threeDSdata.returnUrl,
      );
      this.addFormItem(threeDSPopup, returnForm, 'IsModal', 'true');
      if (threeDSdata.md) {
        this.addFormItem(threeDSPopup, returnForm, 'MD', threeDSdata.md);
      }
    } else if (threeDSdata.threeDSState === ThreeDSState.IDENTIFY) {
      window.localStorage.setItem(
        `cc_data_3ds2token${threeDSdata.invoiceId}`,
        threeDSdata.threeDS2Token,
      );

      const dataObj = {
        threeDSServerTransID: threeDSdata.threeDSServerTransID,
        threeDSMethodNotificationURL: threeDSdata.returnUrl,
      };

      const encodedJSON = this.encodeBase64URL(JSON.stringify(dataObj));
      this.addFormItem(
        threeDSPopup,
        returnForm,
        'threeDSMethodData',
        encodedJSON,
      );
    } else if (threeDSdata.threeDSState === ThreeDSState.CHALLENGE) {
      window.localStorage.setItem(
        `cc_data_3ds2token${threeDSdata.invoiceId}`,
        threeDSdata.threeDS2Token,
      );

      // The `challengeWindowSize` describes the popup window (dimensions?) to
      // the issuer. Can be any of the following flags:
      //   01 - 250px x 400px
      //   02 - 390px x 400px
      //   03 - 500px x 600px
      //   04 - 600px x 400px
      //   05 - 100%  x 100%
      const dataObj = {
        threeDSServerTransID: threeDSdata.threeDSServerTransID,
        acsTransID: threeDSdata.threeDSAcsTransID,
        messageVersion: threeDSdata.threeDSMsgVersion,
        challengeWindowSize: '05',
        messageType: threeDSdata.threeDSMsgType,
      };

      const encodedJSON = this.encodeBase64URL(JSON.stringify(dataObj));
      this.addFormItem(threeDSPopup, returnForm, 'creq', encodedJSON);
    }

    threeDSPopup.document.body.appendChild(returnForm);
    returnForm.submit();
  }

  /**
   * Process 'returnFrom3DS' events.
   *
   * The backend sends the 'returnFrom3DS' events to the popup window, which
   * then gets posted to the main window. Currently the popup window make sure
   * it's DOM is loaded before dispatching the message to main window. If this
   * process is not followed, a race condition could occur when this routine is
   * modifying popup window's DOM while it is being initialised.
   *
   * @param threeDSPopup The popup window.
   * @param eventData Payload sent with the 'returnFrom3DS' event.
   */
  processPostThreeDS(
    threeDSPopup: Window,
    eventData: MessageEvent['data'],
  ): void {
    // Retrieve data saved in
    const data = window.localStorage.getItem(`cc_data${eventData.invoiceId}`);
    localStorage.removeItem(`cc_data${eventData.invoiceId}`);
    const back = window.localStorage.getItem(
      `cc_data_back${eventData.invoiceId}`,
    );
    window.localStorage.removeItem(`cc_data_back${eventData.invoiceId}`);

    // Submit post-3ds form in the popup window
    const returnForm = threeDSPopup.document.createElement('form');
    returnForm.method = 'post';
    returnForm.action = back || '';

    this.addFormItem(threeDSPopup, returnForm, 'fielddummy', '');
    this.addFormItem(
      threeDSPopup,
      returnForm,
      'invoice_id',
      eventData.invoiceId,
    );
    this.addFormItem(
      threeDSPopup,
      returnForm,
      'data',
      decodeURIComponent(data || ''),
    );

    if (eventData.threeDSState === ThreeDSState.REDIRECT) {
      this.addFormItem(threeDSPopup, returnForm, 'PaRes', eventData.paresult);
    } else if (eventData.threeDSState === ThreeDSState.IDENTIFY) {
      const threeDS2Token = window.localStorage.getItem(
        `cc_data_3ds2token${eventData.invoiceId}`,
      );
      window.localStorage.removeItem(`cc_data_3ds2token${eventData.invoiceId}`);

      this.addFormItem(
        threeDSPopup,
        returnForm,
        'threeDS2Token',
        threeDS2Token || '',
      );
      this.addFormItem(
        threeDSPopup,
        returnForm,
        'threeDSServerTransID',
        eventData.threeDSServerTransID || '',
      );
      this.addFormItem(
        threeDSPopup,
        returnForm,
        'threeDSCompInd',
        eventData.threeDSCompInd || '',
      );
    } else if (eventData.threeDSState === ThreeDSState.CHALLENGE) {
      const threeDS2Token = window.localStorage.getItem(
        `cc_data_3ds2token${eventData.invoiceId}`,
      );
      window.localStorage.removeItem(`cc_data_3ds2token${eventData.invoiceId}`);

      this.addFormItem(
        threeDSPopup,
        returnForm,
        'threeDS2Token',
        threeDS2Token || '',
      );
      this.addFormItem(
        threeDSPopup,
        returnForm,
        'threeDSServerTransID',
        eventData.threeDSServerTransID || '',
      );
      this.addFormItem(
        threeDSPopup,
        returnForm,
        'threeDSTransStatus',
        eventData.threeDSTransStatus || '',
      );
    }

    threeDSPopup.document.body.appendChild(returnForm);
    returnForm.submit();
  }

  /**
   * Get authorization payload
   *
   * @param userId user ID for recurring payment
   * @param depositMethod Recurring payment method
   * @param billingAgreementId Billing agreement ID for recurring payment
   * @param amount The amount for recurring payment
   * @param depositCurrencyId The currency ID for deposit
   * @param paymentCurrencyId The currency ID for recurring payment
   * @param threatMetrixSessionId Metrix session ID for tracking purpose
   * @param trackingToken The token for tracking purpose
   * @param cartId The ID for cart that need to be paid
   * @param creditCardGateway Recurring payment gateway
   */
  private getAuthorizationPayload(
    userId: number,
    depositMethod: DepositMethodApi,
    billingAgreementId: string,
    amount: number,
    depositCurrencyId: number,
    paymentCurrencyId: number,
    threatMetrixSessionId: string,
    trackingToken: string,
    creditCardGateway?: string,
    cartId?: number,
  ): ReferenceDepositRequestApi {
    return {
      user_id: userId,
      deposit_method: depositMethod,
      amount,
      currency_id: depositCurrencyId,
      tracking_token: trackingToken,
      recurring_order_id: billingAgreementId,
      threatmetrix_session: threatMetrixSessionId,
      issuing_currency_id: paymentCurrencyId,
      credit_card_gateway: creditCardGateway,
      cart_id: cartId,
    };
  }

  /**
   * Add a form input element.
   *
   * @param windowObj Window object to use call createElement().
   * @param formElement Existing form element to add the input element.
   * @param inputName  Name of the input element.
   * @param inputValue Value of the input element.
   */
  private addFormItem(
    windowObj: Window,
    formElement: HTMLFormElement,
    inputName: string,
    inputValue: string,
  ): void {
    const dataInput = windowObj.document.createElement('input');
    dataInput.setAttribute('name', inputName);
    dataInput.setAttribute('value', inputValue);
    formElement.appendChild(dataInput);
  }

  /**
   * Construct a html form inside the popup and post it to the target location.
   *
   * @param fromAction The third part request url
   * @param formData The form item for third part post request
   */
  private setupAndPostForm(
    formAction: string,
    formData: readonly FormDataItem[],
  ): void {
    if (this.pwa.isNative()) {
      const script = `
        const htmlForm = document.createElement('form');
        htmlForm.method = 'post';
        htmlForm.action = '${formAction}';
        const formData = ${JSON.stringify(formData)};

        formData.forEach(elem => {
          const dataInput = document.createElement('input');
          dataInput.setAttribute('name', elem.name);
          dataInput.setAttribute('value', elem.value);
          htmlForm.appendChild(dataInput);
        });

        document.body.appendChild(htmlForm);
        htmlForm.submit();
      `;

      this.browser.executeScript({ code: script });
    } else if (this.threeDSPopUp !== undefined) {
      const htmlForm = this.threeDSPopUp.document.createElement('form');
      htmlForm.method = 'post';
      htmlForm.action = formAction;
      formData.forEach(elem => {
        this.addFormItem(this.threeDSPopUp, htmlForm, elem.name, elem.value);
      });
      this.threeDSPopUp.document.body.appendChild(htmlForm);
      htmlForm.submit();
    }
  }

  /**
   * Takes a string and encodes it as a base64url string.
   * *Note* Adopted from https://github.com/Adyen/adyen-3ds2-js-utils
   * @param dataStr Input string object.
   */
  private encodeBase64URL(dataStr: string): string {
    const base64 = btoa(dataStr);
    let base64url = base64.split('=')[0]; // Remove any trailing '='s

    base64url = base64url.replace(/\+/g, '-'); // 62nd char of encoding
    base64url = base64url.replace(/\//g, '_'); // 63rd char of encoding

    return base64url;
  }

  /**
   * Make a deposit request to backend for all payment methods that need popup window
   *
   * @param payload Deposit payload for deposit API
   * @param enablePopup If we need show popup window or in-app-browser
   */
  async makeDeposit({
    payload,
    enablePopup,
  }: {
    payload: DepositRequestApi;
    enablePopup: boolean;
  }): Promise<void> {
    const res = await this.deposit(payload).toPromise();
    if (res.status === 'error') {
      this.paymentsMessaging.pushError({
        errorType: PaymentsErrorType.ERROR,
        errorCode: res.errorCode,
        eventId: res.requestId,
      });
      return;
    }
    const { url } = res.result;
    if (!url) {
      this.paymentsMessaging.pushError({
        errorType: PaymentsErrorType.ERROR,
      });
      return;
    }
    if (enablePopup) {
      if (this.pwa.isNative()) {
        this.browser = this.inAppBrowser.create(url, '_blank');
        this.inAppBrowserMessageSubscription = this.browser
          .on('message')
          .subscribe(event => {
            const message = this.paymentsUtils.handleInterframeDepositResult(
              event.data,
            );
            if (message) {
              this.paymentsMessaging.pushMessage(message);
            } else {
              this.handleError(res.requestId);
            }
            this.browser.close();
          });
        await this.browser.on('exit').pipe(take(1)).toPromise();

        this.paymentsMessaging.pushEvent({
          eventType: PaymentsEventType.CLOSE_OVERLAY,
        });
        if (this.inAppBrowserMessageSubscription) {
          this.inAppBrowserMessageSubscription.unsubscribe();
        }
      } else {
        const logo = {
          src: this.paymentsUtils.getMethodLogoPath(payload.deposit_method),
          alt: `${payload.deposit_method} logo`,
        };
        const popup = await this.popUpService.openPopUp(
          `depositPopUp-${payload.deposit_method}`,
        );
        this.paymentsMessaging.pushOverlay({
          brand: logo,
          ctaAction: () => popup.focus(),
          onClose: () => popup.close(),
          overlayType: OverlayTypes.EXTERNAL_POPUP,
        });
        this.setupRedirectionAndListen(popup, res);
        // move Popup logic to this block T218849
      }
    } else if (url.startsWith('https')) {
      this.location.redirect(url);
    } else {
      this.location.navigateByUrl(url);
    }
  }

  /**
   * This will handle all credit card deposit request include 3DS.
   *
   * @param confirmation Deposit confirmation payload for deposit API
   */
  async creditCardDeposit(
    confirmation: DepositRequestConfirmationApi,
  ): Promise<void> {
    // First request
    const depositReference = Date.now();
    this.depositReference = depositReference;
    let depositConfirmation = confirmation;
    let response = await this.depositUpdate(depositConfirmation);

    if (response.status !== 'success') {
      this.handleError(response.requestId, response.errorCode);
      return;
    }
    if (this.handleThreeDSResponse(response)) {
      return;
    }

    // 3DS is triggered
    if (!response.result.threeds_data) {
      return this.handleError(response.requestId);
    }

    let threeDSCtx = this.createThreeDSContext(
      response.result.threeds_data,
      undefined,
    );

    if (!threeDSCtx) {
      return this.handleError(response.requestId);
    }
    // open popup or in-app-browser
    await this.initThreeDSwindow(threeDSCtx.issuerUrl);

    depositConfirmation = await this.generateConfirmationRequest(
      threeDSCtx,
      depositConfirmation,
    );

    // Only process the latest transaction and ignore the others
    if (this.depositReference !== depositReference) {
      return;
    }
    response = await this.depositUpdate(depositConfirmation);
    // 3DS is in progress
    // Received an error response from API
    if (response.status !== 'success') {
      this.handleError(response.requestId, response.errorCode);
      return;
    }

    if (this.handleThreeDSResponse(response)) {
      return;
    }

    // 3DS is triggered
    if (!response.result.threeds_data) {
      return this.handleError(response.requestId);
    }

    threeDSCtx = this.createThreeDSContext(
      response.result.threeds_data,
      threeDSCtx,
    );
    if (!threeDSCtx) {
      return this.handleError(response.requestId);
    }
    // Only process the latest transaction and ignore the others
    if (this.depositReference !== depositReference) {
      return;
    }
    depositConfirmation = await this.generateConfirmationRequest(
      threeDSCtx,
      depositConfirmation,
    );
    // Only process the latest transaction and ignore the others
    if (this.depositReference !== depositReference) {
      return;
    }
    response = await this.depositUpdate(depositConfirmation);
    if (response.status !== 'success') {
      this.handleError(response.requestId, response.errorCode);
      return;
    }
    this.handleThreeDSResponse(response);
  }

  /**
   * initialise the popup window for webapp or in app browser for Capacitor App.
   *
   * @param url default url for popup or in-app-browser
   */
  async initThreeDSwindow(url?: string): Promise<void> {
    this.tracking.trackCustomEvent('ThreeDSRequired');
    if (this.pwa.isNative()) {
      const openUrl = url ?? 'about:blank';
      this.browser = this.inAppBrowser.create(openUrl, '_blank');
      this.browser
        .on('exit')
        .pipe(take(1))
        .toPromise()
        .then(event => {
          this.paymentsMessaging.pushEvent({
            eventType: PaymentsEventType.CLOSE_OVERLAY,
          });
        });
    } else {
      const threeDSPopUp = await this.popUpService.openPopUp('threeDSPopup');
      if (threeDSPopUp) {
        this.threeDSPopUp = threeDSPopUp;
      } else {
        return this.paymentsMessaging.pushError({
          errorType: PaymentsErrorType.ERROR,
          errorMessage: 'Failed to open popup window',
        });
      }
    }
    this.paymentsMessaging.pushOverlay({
      brand: {
        src: this.paymentsUtils.getMethodLogoPath(DepositMethodApi.WIRE),
        alt: 'Bank Logo',
      },
      ctaAction: () => this.popUpService.openPopUp('threeDSPopup'),
      onClose: () => this.cancelThreeDSFlow(),
      overlayType: OverlayTypes.CC_3DS,
    });
  }

  /**
   * generate new confirmation request payload based on the post message
   *
   * @param threeDSCtx 3DS context data object
   * @param confirmation Deposit confirmation payload for deposit API
   */
  async generateConfirmationRequest(
    threeDSCtx: ThreeDSContext,
    confirmation: DepositRequestConfirmationApi,
  ): Promise<DepositRequestConfirmationApi> {
    this.initiate3DSFlow(threeDSCtx);
    let messageData;
    if (this.pwa.isNative()) {
      const message = await this.browser
        .on('message')
        .pipe(take(1))
        .toPromise();
      messageData = message.data;
    } else {
      messageData = await this.getPostThreeDSProgressPromise(threeDSCtx.type);
    }
    if (messageData.threeDSFailure) {
      this.closeOverlay();
      // No need to emit the error as this.paymentsMessaging service seems
      // to magically receive it.
      if (this.pwa.isNative()) {
        this.browser.close();
      } else {
        this.popUpService.closePopUp();
      }
    }
    return {
      user_id: confirmation.user_id,
      deposit_method: DepositMethodApi.FLN_BILLING,
      fln_billing_data: confirmation.fln_billing_data,
      credit_card_gateway: threeDSCtx.ccGateway,
      cart_id: confirmation.cart_id,
      threeds_capability: ThreedsCapabilityApi.VER2,
      invoice_id: threeDSCtx.invoiceId,
      tracking_token: confirmation.tracking_token,
      threeds_response_data: this.constructThreeDSResponseApiData(
        threeDSCtx,
        messageData,
      ),
      action: confirmation.action,
    };
  }

  /**
   * Handle the response from credit card deposit API call.
   *
   * @param response Response from deposit API
   */
  private handleThreeDSResponse(
    response: ResponseData<
      DepositConfirmResponseApi,
      BackendErrorTypes['deposit_confirm'] | 'UNKNOWN_ERROR'
    >,
  ): boolean {
    if (response.status !== 'success') {
      this.handleError(response.requestId, response.errorCode);
      return true;
    }
    if (!response.result.is_threeds_triggered) {
      if (this.browser && this.pwa.isNative()) {
        this.browser.close();
      } else {
        this.popUpService.disableOverlayCloseEvent();
        this.popUpService.closePopUp();
      }
      // Deposit is successful.
      this.paymentsMessaging.pushResult({
        paymentsStatus: PaymentsResultStatus.SUCCESS,
      });
      return true;
    }
    // 3DS is triggered
    if (!response.result.threeds_data) {
      this.handleError(response.requestId);
      return true;
    }
    return false;
  }

  /**
   * Close overlay
   */
  private closeOverlay(): void {
    this.paymentsMessaging.pushEvent({
      eventType: PaymentsEventType.CLOSE_OVERLAY,
    });
  }

  /**
   * Handle the error response from API call
   *
   * @param eventId Event ID from API response
   * @param errorMessage The error message from the API response
   */
  private handleError(eventId?: string, errorCode?: string): void {
    this.closeOverlay();
    if (this.pwa.isNative()) {
      this.browser.close();
    } else {
      this.popUpService.closePopUp();
    }
    this.paymentsMessaging.pushError({
      errorCode,
      errorType: errorCode
        ? PaymentsErrorType.ERROR
        : PaymentsErrorType.ERROR_UNKNOWN,
      eventId,
    });
  }

  /**
   * Get result post message from GAF
   */
  getThreeDSDepositResultPromise(): Promise<MessageEvent['data']> {
    return this.paymentsUtils
      .getFilteredMessageStream(
        event => !!event.data.threeDSFailure || !!event.data.threeDSSuccess,
      )
      .pipe(take(1))
      .toPromise();
  }

  /**
   * Get return post message from GAF
   */
  getPostThreeDSPromise(): Promise<MessageEvent['data']> {
    return this.paymentsUtils
      .getFilteredMessageStream(
        event =>
          !!event.data.threeDSFailure ||
          (!!event.data.type && event.data.type === ThreeDSMessageType.RETURN),
      )
      .pipe(take(1))
      .toPromise();
  }

  /**
   * Get 3DS post message based on 3DS state.
   *
   * @param threeDSState 3DS state for different flows
   */
  private getPostThreeDSProgressPromise(
    threeDSState: string,
  ): Promise<MessageEvent['data']> {
    return this.paymentsUtils
      .getFilteredMessageStream(
        event =>
          event.data.threeDSState === threeDSState &&
          (!!event.data.threeDSFailure ||
            (!!event.data.type &&
              event.data.type === ThreeDSMessageType.RETURN)),
      )
      .pipe(take(1))
      .toPromise();
  }

  /**
   * Emit error message to show the banner in deposit page
   *
   * @param errorMessage Error message from API response
   * @param errorType Error type from API response
   */
  emitErrorMessage(
    errorMessage: string,
    errorType:
      | PaymentsErrorType.ERROR
      | PaymentsErrorType.ERROR_AUTH
      | PaymentsErrorType.ERROR_INIT
      | PaymentsErrorType.ERROR_UNKNOWN,
  ): void {
    this.paymentsMessaging.pushError({
      errorMessage,
      errorType,
    });
  }

  /**
   * Cancel the 3DS flow
   */
  cancelThreeDSFlow(): void {
    if (this.pwa.isNative()) {
      this.browser.close();
    } else {
      this.popUpService.closePopUp();
    }
    this.paymentsMessaging.pushResult({
      paymentsStatus: PaymentsResultStatus.INTERRUPTED,
    });
  }

  /**
   *  Function to pop the threeds modal
   *  1. Pop overlay to prompt user - popThreeDSModal()
   *  2. Trigger 3DS popup if user continue with the 3DS flow - initThreeDSFlow()
   *  3. Handle the result from the bank after the user completing the 3DS flow - processThreeDSResults()
   *  4. Continue 3DS flow if challenged further, or finalise the flow - finaliseOrContinueThreeDSFlow()
   *  5. Function to cancel the 3DS flow - cancelThreeDSFlow()
   *
   * @param threeDSdata PostMessage data from Gaf
   */
  popThreeDSModal(threeDSdata: MessageEvent['data']): void {
    const logo = {
      src: this.paymentsUtils.getMethodLogoPath(DepositMethodApi.WIRE),
      alt: 'Bank Logo',
    };

    this.tracking.trackCustomEvent('ThreeDSRequired');
    this.paymentsMessaging.pushOverlay({
      brand: logo,
      ctaAction: () => this.initThreeDSFlow(threeDSdata),
      onClose: () => this.cancelThreeDSFlow(),
      overlayType: OverlayTypes.CC_3DS,
    });
  }

  /**
   *  CTA callback function to initiliase the 3DS process from the 3DS modal
   *
   * @param threeDSdata  PostMessage data from Gaf
   */
  initThreeDSFlow(threeDSdata: MessageEvent['data']): void {
    this.popUpService.openPopUp('threeDSPopup').then(threeDSPopup => {
      this.getPostThreeDSPromise().then(data =>
        this.processThreeDSResults(threeDSPopup, data),
      );

      this.processThreeDSForm(threeDSPopup, threeDSdata);
    });
  }

  /**
   *  Continue 3DS flow if challenged further, or finalise the flow.
   *
   * @param threeDSPopup Window object for 3DS popup
   * @param eventData PostMessage data from Gaf
   */
  finaliseOrContinueThreeDSFlow(
    threeDSPopup: Window,
    eventData: MessageEvent['data'],
  ): void {
    if (!!eventData.type && eventData.type === ThreeDSMessageType.TRIGGER) {
      this.getPostThreeDSPromise().then(data =>
        this.processThreeDSResults(threeDSPopup, data),
      );
      this.processThreeDSForm(threeDSPopup, eventData);
    } else {
      if (!!eventData.type && eventData.type === 'success') {
        // Disable overlay being closed immediately after the popup window
        // is closed. The overlay displays additional info and has logic to
        // redirect the user to dashboard.
        this.popUpService.disableOverlayCloseEvent();
        this.paymentsMessaging.pushResult({
          paymentsStatus: PaymentsResultStatus.SUCCESS,
        });
      } else {
        this.closeOverlay();
        this.emitErrorMessage('3DS deposit failed', PaymentsErrorType.ERROR);
      }

      if (this.pwa.isNative()) {
        this.browser.close();
      } else {
        this.popUpService.closePopUp();
      }
    }
  }

  /**
   *  Handling a result message from 3DS flow
   *
   * @param threeDSPopup Window object for 3DS popup
   * @param eventData PostMessage data from Gaf
   */
  processThreeDSResults(
    threeDSPopup: Window,
    eventData: MessageEvent['data'],
  ): void {
    if (eventData.threeDSFailure) {
      this.closeOverlay();
      // No need to emit the error as this.paymentsMessaging service seems
      // to magically receive it.
      if (this.pwa.isNative()) {
        this.browser.close();
      } else {
        this.popUpService.closePopUp();
      }
    } else {
      this.getThreeDSFinaliseOrContinuePromise().then(resultData =>
        this.finaliseOrContinueThreeDSFlow(threeDSPopup, resultData),
      );
      this.processPostThreeDS(threeDSPopup, eventData);
    }
  }

  /**
   *  TODO [PSD2]: Is there a better way to restructure these promises?
   *  In the new 3DS flows, a subsequent trigger message (for challenge)
   *  could be sent after the initial trigger message for redirect.
   */
  getThreeDSFinaliseOrContinuePromise(): Promise<MessageEvent['data']> {
    return this.paymentsUtils
      .getFilteredMessageStream(
        event =>
          !!event.data.threeDSFailure ||
          !!event.data.threeDSSuccess ||
          (!!event.data.type && event.data.type === ThreeDSMessageType.TRIGGER),
      )
      .pipe(take(1))
      .toPromise();
  }

  /**
   *  Message hooks
   */
  getThreeDSTriggerStream(): Rx.Observable<MessageEvent['data']> {
    return this.paymentsUtils.getFilteredMessageStream(
      event =>
        !!event.data.type && event.data.type === ThreeDSMessageType.TRIGGER,
    );
  }

  /**
   * setup popup window for third part payment methods.
   * @param popup Window object for 3DS popup
   * @param response Deposit API response
   */
  private setupRedirectionAndListen(
    popup: Window,
    response: SuccessResponseData<DepositRequestActionApi>,
  ): void {
    const { url } = response.result;
    if (!url) {
      this.paymentsMessaging.pushError({ errorType: PaymentsErrorType.ERROR });
      return;
    }

    // redirect and listen for deposit result
    popup.location.href = url;
    this.paymentsUtils.listenForDepositResult().then(data => {
      popup.close();
      const message = this.paymentsUtils.handleInterframeDepositResult(data);
      if (message) {
        this.paymentsMessaging.pushMessage(message);
      } else {
        this.handleError(response.requestId);
      }
    });
  }

  processUpgradesAndMilestones(
    requestData: PreprocessMilestonesRequest,
  ): Promise<PreprocessMilestonesResponse | undefined> {
    return this.freelancerHttp
      .post<PreprocessMilestonesResponse>(
        `payment/on-preprocess-upgrades-and-milestones.php`,
        requestData,
        {
          isGaf: true,
          serializeBody: true,
        },
      )
      .pipe(
        take(1),
        map(response =>
          response.status === 'success' ? response.result : undefined,
        ),
      )
      .toPromise();
  }

  /**
   * Temporary function to check if use Capacitor App.
   */
  isNative(): boolean {
    return this.pwa.isNative();
  }
}
